import { Link } from '@tanstack/react-router';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { FOCUS_RING } from '@/shared/components/pillClasses';
import { formatElapsedSince } from '@/shared/utils/time';
import type { QueuedRow } from '@/features/tasks';

import {
  meterName,
  meterSentence,
  ordinal,
  type Capacity,
} from '../queueWords';
import { CapacitySlots } from './CapacitySlots';

/** Enough of the line to act on; the rest is the Tasks page's Queued filter. */
const SHOWN_IN_LINE = 5;

export interface CapacityMeterProps {
  /** Absent from an older service: then there is no meter, and no space kept for one. */
  capacity: Capacity | null | undefined;
  /** On a workspace page the meter is a button that opens onto the queue. */
  workspaceId?: string;
  /** The workspace's waiting runs in order; read only when the popover opens. */
  queue?: () => readonly QueuedRow[];
  /** Organization administrators get the way to this workspace's limits. */
  isAdmin?: boolean;
  /** What the slots count: "Runs" on a workspace, the installation on the console. */
  label?: string;
}

function MeterFace({ capacity, label }: { capacity: Capacity; label: string }) {
  return (
    <>
      <span className="text-muted-foreground">{label}</span>
      <span className="hidden sm:inline-flex">
        <CapacitySlots
          running={capacity.running}
          limit={capacity.limit}
          queued={capacity.queued}
          size="md"
          decorative
        />
      </span>
      <span className="text-foreground font-medium tabular-nums">
        {`${String(capacity.running)} of ${String(capacity.limit)}`}
      </span>
      {capacity.queued > 0 ? (
        <span className="text-primary font-medium tabular-nums">
          {`+${String(capacity.queued)} waiting`}
        </span>
      ) : null}
    </>
  );
}

const FACE =
  'border-border bg-card inline-flex max-w-full items-center gap-2.5 rounded-lg border px-3 py-1.5 text-xs whitespace-nowrap pointer-coarse:min-h-11';

/**
 * How busy the workspace is, always in the page header (spec 118 US1): the run slots
 * drawn — filled for running, dashed past the limit mark for waiting — with the
 * figures beside them. It replaces the queue notice, which said nothing until the
 * workspace was already full and then said it as a banner.
 *
 * On the console it is the same face for the installation, as a figure: there is no
 * one queue there to open.
 */
export function CapacityMeter({
  capacity,
  workspaceId,
  queue,
  isAdmin = false,
  label = 'Runs',
}: CapacityMeterProps) {
  if (!capacity) return null;
  const name = meterName(capacity);

  if (workspaceId === undefined) {
    return (
      <span role="img" aria-label={name} className={FACE}>
        <MeterFace capacity={capacity} label={label} />
      </span>
    );
  }

  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label={name}
            className={`${FACE} hover:bg-accent ${FOCUS_RING} transition-colors`}
          />
        }
      >
        <MeterFace capacity={capacity} label={label} />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        aria-label="Runs at once"
        className="flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-3 p-4"
      >
        <QueuePanel
          capacity={capacity}
          workspaceId={workspaceId}
          queue={queue}
          isAdmin={isAdmin}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * The popover's body (spec 118 US2): the figure, why runs wait, and who waits in
 * the order they will start. Mounted only while open, so the line is read then.
 *
 * Deliberately no way to change a limit here (FR-007): an administrator is sent to
 * the workspace's sheet on the Limits page, the one place a limit changes.
 */
function QueuePanel({
  capacity,
  workspaceId,
  queue,
  isAdmin,
}: {
  capacity: Capacity;
  workspaceId: string;
  queue: (() => readonly QueuedRow[]) | undefined;
  isAdmin: boolean;
}) {
  const line = capacity.queued > 0 && queue ? queue() : [];
  const shown = line.slice(0, SHOWN_IN_LINE);
  const more = line.length - shown.length;

  return (
    <>
      <div className="flex items-end justify-between gap-3">
        <p className="text-foreground text-sm tabular-nums">
          <span className="text-2xl leading-none font-light">
            {String(capacity.running)}
          </span>{' '}
          <span className="text-muted-foreground">
            {`of ${String(capacity.limit)} running`}
          </span>
        </p>
        <CapacitySlots
          running={capacity.running}
          limit={capacity.limit}
          queued={capacity.queued}
          size="md"
          decorative
        />
      </div>
      <p className="text-muted-foreground text-sm">{meterSentence(capacity)}</p>

      {shown.length > 0 ? (
        <ol className="flex flex-col gap-2">
          {shown.map((row) => (
            <li
              key={row.taskId}
              className="grid grid-cols-[2.25rem_minmax(0,1fr)] items-baseline gap-2 text-sm"
            >
              <span className="text-primary font-mono font-bold">
                {row.position === null ? '' : ordinal(row.position)}
              </span>
              <span className="flex min-w-0 flex-col">
                {row.hasBoard ? (
                  <Link
                    to="/workspaces/$workspaceId/workflows/$workflowKey/board"
                    params={{ workspaceId, workflowKey: row.workflowKey }}
                    search={{ task: row.taskId, tab: 'details' as const }}
                    className="text-foreground truncate font-medium hover:underline"
                  >
                    {row.title}
                  </Link>
                ) : (
                  <span className="text-foreground truncate font-medium">
                    {row.title}
                  </span>
                )}
                <span className="text-muted-foreground truncate text-xs">
                  {`${row.workflowName} · ${row.stepLabel ?? 'No step on this board'}`}
                  {row.queuedAt
                    ? ` · waiting ${formatElapsedSince(row.queuedAt)}`
                    : ''}
                </span>
              </span>
            </li>
          ))}
        </ol>
      ) : null}

      {more > 0 || isAdmin ? (
        <div className="border-border flex items-center justify-between gap-3 border-t pt-3 text-xs">
          {more > 0 ? (
            <Link
              to="/workspaces/$workspaceId/tasks"
              params={{ workspaceId }}
              search={{ state: 'queued' }}
              className="text-primary font-medium hover:underline"
            >
              {`+${String(more)} more`}
            </Link>
          ) : (
            <span />
          )}
          {isAdmin ? (
            <Link
              to="/admin/limits"
              search={{ workspace: workspaceId }}
              className="text-foreground font-medium underline underline-offset-2"
            >
              Change limits
            </Link>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
